import * as React from 'react'

import { cn } from '@repo/ui/lib/utils'
import {
  assertMaskPreservesDraft,
  caretAfterSignificant,
  countSignificantBefore,
  significantPositions,
  type FieldMask,
} from '../../lib/fieldMask'
import { Input } from './Input'

type MaskedFieldProps<TValue> = {
  /** What kind of value this field edits, and how to write it. See {@link FieldMask}. */
  mask: FieldMask<TValue>
  value: TValue | null
  /** Fires on every keystroke, not only on blur. */
  onValueChange: (next: TValue | null) => void
  id?: string
  disabled?: boolean
  placeholder?: string
  className?: string
  onBlur?: () => void
  'aria-label'?: string
  'aria-invalid'?: boolean
}

/**
 * A text field that stays formatted while it is being typed into.
 *
 * `type="text"` whatever the mask: a number input cannot display grouped or currency-formatted
 * text, and brings spinner arrows that are meaningless on most values.
 *
 * The mask is applied on every keystroke, so every edit reformats the text under the caret. The
 * caret is therefore re-derived from the significant characters around it rather than from a
 * character offset (see {@link caretAfterSignificant}), which would drift every time a separator
 * appeared or vanished.
 */
function MaskedField<TValue>({
  mask,
  value,
  onValueChange,
  id,
  disabled,
  placeholder,
  className,
  onBlur,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: MaskedFieldProps<TValue>) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  /** Where to put the caret once the reformatted text has been painted. */
  const caretRef = React.useRef<number | null>(null)
  /** The value as typed, unmasked. Only meaningful while focused. */
  const [draft, setDraft] = React.useState('')
  const [isEditing, setIsEditing] = React.useState(false)

  // Idle, the field renders straight off `value`, so a parent write lands without a sync step.
  // Focused, it renders the draft, which keeps the trailing separator and zeros that formatting
  // the parsed value would discard.
  const displayValue = isEditing ? mask.render(draft) : value === null ? '' : mask.display(value)

  React.useLayoutEffect(() => {
    const caret = caretRef.current
    if (caret === null) return
    caretRef.current = null
    inputRef.current?.setSelectionRange(caret, caret)
  })

  /** Land an edit — raw field text plus caret — masked, with the caret behind the same
   *  significant characters it was before. */
  const applyEdit = (raw: string, caretInRaw: number) => {
    const significantBefore = countSignificantBefore(raw, caretInRaw, mask.isSignificant)
    const next = mask.fromText(raw)
    assertMaskPreservesDraft(mask, next)
    setDraft(next)
    caretRef.current = caretAfterSignificant(
      mask.render(next),
      significantBefore,
      mask.isSignificant,
    )
    onValueChange(mask.parse(next))
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setDraft(value === null ? '' : mask.fromValue(value))
    setIsEditing(true)
    event.target.select()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    applyEdit(event.target.value, event.target.selectionStart ?? event.target.value.length)
  }

  /**
   * Backspace and Delete have to reach past the mask's own characters: with the caret just after a
   * grouping separator the browser would delete the separator, which the next reformat puts
   * straight back, so the key appears to do nothing.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return
    const input = event.currentTarget
    const { selectionStart, selectionEnd } = input
    if (selectionStart === null || selectionEnd === null) return
    // A range selection deletes exactly what is highlighted; the default is already right.
    if (selectionStart !== selectionEnd) return

    const shown = input.value
    const editable = significantPositions(shown, mask.isSignificant)
    const target =
      event.key === 'Backspace'
        ? editable.filter((index) => index < selectionStart).pop()
        : editable.find((index) => index >= selectionStart)
    if (target === undefined) return

    // The adjacent character is already part of the value, so the browser is already right.
    const isAdjacent =
      event.key === 'Backspace' ? target === selectionStart - 1 : target === selectionStart
    if (isAdjacent) return

    event.preventDefault()
    applyEdit(shown.slice(0, target) + shown.slice(target + 1), target)
  }

  const handleBlur = () => {
    // Every keystroke already went up, so this only drops the draft for the idle display.
    setIsEditing(false)
    onBlur?.()
  }

  return (
    <Input
      ref={inputRef}
      id={id}
      type="text"
      inputMode={mask.inputMode}
      autoComplete="off"
      className={cn('text-end font-mono', className)}
      value={displayValue}
      placeholder={placeholder}
      onFocus={handleFocus}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
    />
  )
}

export { MaskedField }
